// utils.jsx — Shared utility helpers for Lift Log
//
// Pure ESM module: no React, no DOM, no default export.
// Safe to import in Node smoke harnesses via data: URL.

/**
 * parseYouTube(url) → { id: string, start: number } | null
 *
 * Extracts the YouTube video ID and optional start time (seconds) from any
 * common YouTube URL form. Returns null for invalid or non-YouTube input.
 *
 * XSS boundary: the embed URL is always built from the extracted { id, start }
 * values — the raw input string never appears in an iframe src or anchor href.
 * A crafted "javascript:" URL fails the YouTube-ID match and returns null.
 *
 * Accepted URL forms:
 *   https://youtu.be/<id>
 *   https://youtu.be/<id>?t=1m30s
 *   https://www.youtube.com/watch?v=<id>
 *   https://youtube.com/watch?list=PLx&v=<id>
 *   https://www.youtube.com/shorts/<id>
 *   https://www.youtube.com/embed/<id>?start=90
 *   https://m.youtube.com/watch?v=<id>
 *   youtu.be/<id>  (no protocol)
 *
 * Start-time forms accepted: ?t=90, ?t=90s, ?t=1m30s, ?t=1h2m3s, ?t=1m, &start=90
 */
export function parseYouTube(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  if (!s) return null;

  // Match the video ID from all common URL forms.
  // Covers: youtu.be/<id>  youtube.com/watch?(...&)?v=<id>
  //         youtube.com/shorts/<id>  youtube.com/embed/<id>  youtube.com/v/<id>
  // Optional leading https?:// and www.|m. prefix are ignored (not part of capture).
  const idRe = /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:[^#]*&)?v=|shorts\/|embed\/|v\/))([\w-]{5,})/i;
  const m = s.match(idRe);
  if (!m) return null;
  const id = m[1];

  // Extract start time.
  // Supports: ?t=90, ?t=90s, ?t=1m30s, ?t=1h2m3s, ?t=1m, &start=90
  let start = 0;
  const tMatch = s.match(/[?&](?:t|start)=([^\s&#]+)/i);
  if (tMatch) {
    const raw = tMatch[1];
    // Pure integer, optionally followed by 's'
    const plain = raw.match(/^(\d+)s?$/);
    if (plain) {
      start = parseInt(plain[1], 10);
    } else {
      // HMS form: 1h2m3s, 1m30s, 2m, 30s, etc.
      let secs = 0;
      const hh = raw.match(/(\d+)h/); if (hh) secs += parseInt(hh[1], 10) * 3600;
      const mm = raw.match(/(\d+)m/); if (mm) secs += parseInt(mm[1], 10) * 60;
      const ss = raw.match(/(\d+)s/); if (ss) secs += parseInt(ss[1], 10);
      start = secs;
    }
  }

  return { id, start };
}

/**
 * prescriptionFor(program, pe) → { sets, repMin, repMax, rest, rpeEarly, rpeLast, notes [, name, yt, alternates] }
 *
 * Returns the active week's prescription for pe from program.periodization,
 * falling back to pe inline fields when the program is non-periodized or
 * when the current week has no override for this PE.
 *
 * Fallback conditions (all return _peInline(pe)):
 *   1. program.periodization is absent/null
 *   2. periodization.weeks is empty or not an array
 *   3. periodization.currentWeek is out of range
 *   4. week.prescriptions[pe.id] is absent
 *
 * When an override exists, each field is merged independently:
 *   sets/repMin/repMax/rest  → override wins if present, else pe inline
 *   rpeEarly/rpeLast/notes   → override wins if present, else null
 *
 * Optional per-week identity overrides (Phase 34 — Ramping rotation support):
 *   name       → included only when ov.name != null (else key is absent)
 *   yt         → included only when ov.yt != null (else key is absent)
 *   alternates → included only when ov.alternates != null (else key is absent)
 *
 * Absent identity-override keys guarantee backward compatibility: the caller
 * spread { ...pe, ...prescriptionFor(...) } leaves pe.name / pe.yt /
 * pe.alternates intact for non-rotating programs and Foundation weeks.
 *
 * targetWeight is intentionally absent from the return shape — the spread
 * { ...pe, ...prescriptionFor(...) } in workout.jsx preserves pe.targetWeight
 * (user progressive load) automatically.
 *
 * Overrides are keyed by pe.id (unique per slot), never pe.exerciseId.
 * This function is null-safe: program and pe may be undefined.
 */
export function prescriptionFor(program, pe) {
  if (!pe) return _peInline(pe);          // null-safe (BL-01): undefined pe → zero-prescription
  const p = program?.periodization;
  if (!p || !Array.isArray(p.weeks) || p.weeks.length === 0) return _peInline(pe);
  const idx = typeof p.currentWeek === 'number' ? p.currentWeek : 0;
  const week = p.weeks[idx];
  if (!week) return _peInline(pe);
  const ov = week.prescriptions?.[pe.id] ?? null;
  if (!ov) return _peInline(pe);
  return {
    sets:     ov.sets     ?? pe.sets,
    repMin:   ov.repMin   ?? pe.repMin,
    repMax:   ov.repMax   ?? pe.repMax,
    rest:     ov.rest     ?? pe.rest,
    rpeEarly: ov.rpeEarly ?? null,
    rpeLast:  ov.rpeLast  ?? null,
    notes:    ov.notes    ?? null,
    // Phase 34: per-week identity overrides (optional — absent → pe's own identity used)
    // WR-03: also reject '' so an empty-string override never blanks out the canonical name/yt
    ...(ov.name       != null && ov.name !== ''  ? { name:       ov.name       } : {}),
    ...(ov.yt         != null && ov.yt   !== ''  ? { yt:         ov.yt         } : {}),
    ...(ov.alternates != null                    ? { alternates: ov.alternates } : {}),
  };
}

function _peInline(pe) {
  // Optional-chained so an undefined pe yields a safe all-undefined/null shape
  // instead of throwing (the JSDoc null-safety contract).
  return {
    sets: pe?.sets, repMin: pe?.repMin, repMax: pe?.repMax, rest: pe?.rest,
    rpeEarly: null, rpeLast: null, notes: null,
  };
}

// Working-set count is clamped to this inclusive range when added/removed on the fly.
export const MIN_SETS = 1;
export const MAX_SETS = 12;

/**
 * withSetCount(program, dayId, peId, count) → new program (immutable copy)
 *
 * Persists a new working-set count for one program-exercise (peId, inside dayId).
 * This is the data half of the mid-workout "add / remove a set" feature: the count
 * the user lands on is remembered for next time. `count` is clamped to
 * [MIN_SETS, MAX_SETS].
 *
 * The write target depends on whether the program is periodized — and must match
 * exactly where prescriptionFor() READS the set count from, or the change wouldn't
 * stick:
 *
 *   • Non-periodized program (no periodization.weeks) → writes pe.sets inline,
 *     since prescriptionFor falls back to _peInline(pe).sets.
 *
 *   • Periodized program → writes an explicit `sets` override into the ACTIVE week
 *     (periodization.currentWeek) AND every LATER week's prescription for this pe.
 *     Earlier weeks are left untouched, so the planned ramp up to the current block
 *     is preserved while the change carries forward ("this + all future weeks").
 *     pe.sets inline is deliberately NOT touched for periodized programs: resolution
 *     flows through the per-week overrides, and changing the inline default would
 *     leak into past weeks that resolve via fallback.
 *
 * Null-safe: returns `program` unchanged when program/day/pe can't be resolved.
 */
export function withSetCount(program, dayId, peId, count) {
  if (!program || !Array.isArray(program.days)) return program;
  const n = Math.max(MIN_SETS, Math.min(MAX_SETS, Math.round(count || MIN_SETS)));
  const per = program.periodization;
  const isPeriodized = !!per && Array.isArray(per.weeks) && per.weeks.length > 0;

  if (!isPeriodized) {
    return {
      ...program,
      days: program.days.map(d => d.id !== dayId ? d : {
        ...d,
        exercises: (d.exercises || []).map(e => e.id !== peId ? e : { ...e, sets: n }),
      }),
    };
  }

  const curWeek = typeof per.currentWeek === 'number' ? per.currentWeek : 0;
  const weeks = per.weeks.map((wk, i) => {
    if (i < curWeek) return wk; // past weeks untouched — preserve the planned ramp
    const prescriptions = { ...(wk?.prescriptions || {}) };
    prescriptions[peId] = { ...(prescriptions[peId] || {}), sets: n };
    return { ...wk, prescriptions };
  });
  return { ...program, periodization: { ...per, weeks } };
}

/**
 * weightIncrementFor(pe) → number (lb, the app's internal unit)
 *
 * The double-progression weight step for an exercise: small isolation muscles and
 * timed/core work step by 2.5, everything else by 5. Single source of truth shared
 * by store.completeSession (auto-progression on finish) and the active-workout
 * prefill (progressionSuggestion) so the two never drift.
 */
export function weightIncrementFor(pe) {
  if (!pe) return 5;
  if (pe.incLb > 0) return pe.incLb; // unit-aware override (2.5 kg / 1.25 kg steps for metric users)
  if (pe.group === 'Core' || pe.unit === 'sec') return 2.5;
  if (pe.group === 'Shoulders' || pe.group === 'Biceps' || pe.group === 'Triceps') return 2.5;
  return 5;
}

/**
 * parseWeightInput(str) → number | null
 *
 * Parses arbitrary keyboard text into a non-negative weight number.
 * This is the sole untrusted-input choke point for the pure weight layer (T-37-01).
 *
 * Rules:
 *   - Comma-locale decimals ('9,5') are normalised to '9.5' before parsing.
 *   - Trailing-dot partials ('90.') are valid — parseFloat strips the dot cleanly.
 *   - Returns null for empty/whitespace, non-numeric, or negative input.
 *   - Never snaps the value to the 2.5/5 increment grid — typed '9' returns exactly 9.
 */
export function parseWeightInput(str) {
  if (str === null || str === undefined) return null;
  const s = String(str).trim().replace(',', '.');
  if (!s) return null;
  const n = parseFloat(s);
  if (!isFinite(n) || n < 0) return null;
  return n;
}

/**
 * nudgeWeight(w, dir, pe) → number
 *
 * Applies a relative ±weightIncrementFor(pe) delta to the current weight w.
 * Never rounds the base value to the increment grid — from a typed '9', '+' → 11.5,
 * '−' → 6.5 (T-37-02 mitigation: relative delta, off-grid values survive stepping).
 *
 * w   — current weight (any non-negative number, may be off-grid)
 * dir — '+' or '-'
 * pe  — program-exercise (passed to weightIncrementFor for the per-exercise step)
 *
 * Returns Math.max(0, w ± inc) rounded to 2 decimals to kill float drift.
 * Lower bound clamps at 0 — no negative weights.
 */
export function nudgeWeight(w, dir, pe) {
  const inc = weightIncrementFor(pe);
  const next = dir === '+' ? w + inc : w - inc;
  return Math.max(0, +next.toFixed(2));
}

/**
 * nudgeReps(reps, dir) → number
 *
 * Applies a ±1 delta to the current rep count.
 * Floor at 1 — reps cannot go below 1.
 *
 * reps — current rep count (integer)
 * dir  — '+' or '-'
 */
export function nudgeReps(reps, dir) {
  return dir === '+' ? reps + 1 : Math.max(1, reps - 1);
}

/**
 * sliderMaxFor(w) → number
 *
 * Computes the upper bound for the weight slider as Math.max(2 × w, 100).
 * Scales with the load to keep resolution useful at any weight, while the
 * 100 baseline ensures the slider is never uselessly short for light or zero loads.
 *
 * w — current or target weight (any non-negative number)
 */
export function sliderMaxFor(w) {
  return Math.max(2 * (Number(w) || 0), 100);
}

/**
 * progressionSuggestion(last, pe) → { w, reps, fromHistory }
 *
 * Double-progression prefill for the next time you perform a set. Given `last`
 * (the { w, reps } you logged for this exercise+set in your most recent completed
 * session, or null when there's no history) and the resolved `pe` (carrying the
 * active week's repMin/repMax plus group/unit for the weight step):
 *
 *   • no history          → program target weight + mid-range reps (fromHistory:false)
 *   • below the rep top    → SAME weight, one more rep        (e.g. 8→9 @ 120)
 *   • at/above the rep top → weight + weightIncrementFor(pe), and JUST enough reps
 *                            that TOTAL weight lifted (w × reps) still beats last
 *                            time — never a lower-volume "reset".
 *
 * Progressive-overload guarantee: whenever there is history, the suggested total
 * volume (w × reps) is strictly greater than last time. The old scheme reset reps
 * to repMin on a weight bump (e.g. 120×10=1200 → 125×8=1000), which LOWERED total
 * weight lifted; that regressed overload and is what this avoids.
 *
 * Weight is rounded to 2 decimals to avoid float drift from the increment add.
 */
export function progressionSuggestion(last, pe) {
  const repMin = pe?.repMin ?? 8;
  const repMax = pe?.repMax ?? 10;
  if (!last || typeof last.w !== 'number') {
    return { w: pe?.targetWeight || 0, reps: Math.round((repMin + repMax) / 2), fromHistory: false };
  }
  const lastReps = typeof last.reps === 'number' ? last.reps : repMax;
  // Below the top of the rep range → same weight, one more rep (volume ↑).
  if (lastReps < repMax) {
    return { w: last.w, reps: lastReps + 1, fromHistory: true };
  }
  // At/above the top → add weight, then keep the SMALLEST rep count that still
  // beats last time's total volume (progressive overload by total weight lifted),
  // clamped to at least repMin. repsForOverload ≤ lastReps because the weight went
  // up, so this never inflates reps beyond what was already performed.
  const newW = +(last.w + weightIncrementFor(pe)).toFixed(2);
  const lastVolume = last.w * lastReps;
  const repsForOverload = Math.floor(lastVolume / newW) + 1; // smallest reps with newW*reps > lastVolume
  const reps = Math.max(repMin, Math.min(lastReps, repsForOverload));
  return { w: newW, reps, fromHistory: true };
}

/**
 * resolvePrefill({ pe, setIdx, sessionSets, lastPerfFor, mode })
 *   → { w, reps, rpe?, fromHistory, tier } | null
 *
 * Two-tier dispatcher that computes the pre-fill value for a set before the user logs it.
 *
 * pe          — resolved program-exercise (must carry exerciseId, repMin, repMax,
 *               targetWeight, group, unit — via { ...pe, ...prescriptionFor(prog, pe) })
 * setIdx      — 0-based index of the set being pre-filled
 * sessionSets — session.sets pre-filtered by the caller to this pe's peId
 *               (i.e. session.sets.filter(s => s.peId === pe.id)); read-only
 * lastPerfFor — (exerciseId, setIdx) => { w, reps } | null — cross-session lookup
 *               injected by Phase 39 caller; null-safe (may be undefined)
 * mode        — prefs.progression.mode; 'double' applies double-progression (default)
 *
 * Tier 1 (session mirror): setIdx > 0 AND a logged set with setIdx < requested exists
 *   in sessionSets → exact copy (w, reps, rpe); fromHistory: false; tier: 'session'.
 *   Highest prior setIdx wins — handles add-on-the-fly gaps (not hard-coded setIdx-1).
 *
 * Tier 2 (double progression): no within-session prior → call lastPerfFor(pe.exerciseId, setIdx);
 *   last.w > 0 → delegate to progressionSuggestion(last, pe); tier: 'cross-session'.
 *
 * Guard (no-progression): last === null OR last.w === 0 → delegate to
 *   progressionSuggestion(null, pe) → { w: targetWeight||0, reps: mid, fromHistory: false };
 *   tier: 'no-history'. Prevents nonsensical weight progression for bodyweight exercises.
 *
 * Returns null when pe is missing (null-safe contract matching prescriptionFor).
 */
export function resolvePrefill({ pe, setIdx, sessionSets, lastPerfFor, mode }) {
  if (!pe) return null;

  // mode 'off': user types from scratch — no suggestion, no Tier 1 mirror.
  if (mode === 'off') {
    return { w: 0, reps: 0, fromHistory: false, tier: null };
  }

  // ── Tier 1: within-session mirror ──────────────────────────────────────────
  if (setIdx > 0 && Array.isArray(sessionSets) && sessionSets.length > 0) {
    // Find the highest-indexed logged set whose setIdx < requested setIdx.
    // Handles add-on-the-fly gaps where setIdx-1 may not have been logged yet.
    const prior = sessionSets
      .filter(s => typeof s.setIdx === 'number' && s.setIdx < setIdx)
      .sort((a, b) => b.setIdx - a.setIdx)[0];
    if (prior) {
      return {
        w: prior.w,
        reps: prior.reps,
        rpe: prior.rpe ?? null,
        fromHistory: false,
        tier: 'session',
      };
    }
  }

  // ── Tier 2: cross-session double-progression ────────────────────────────────
  // Keyed by pe.exerciseId (library id) — distinct from Tier 1 which uses pe.id (slot id).
  const last = typeof lastPerfFor === 'function'
    ? (lastPerfFor(pe.exerciseId, setIdx) ?? null)
    : null;

  // Guard: no history or bodyweight (w=0) — no weight-based progression.
  // Delegates to progressionSuggestion(null, pe) → targetWeight + mid-range reps.
  // This avoids the pitfall where progressionSuggestion({w:0}, pe) would return w:2.5.
  if (!last || last.w === 0) {
    return { ...progressionSuggestion(null, pe), tier: 'no-history' };
  }

  // mode 'last': mirror cross-session raw values; no progression advance.
  // Distinct tier 'last' (not 'cross-session') drives the non-"Progressed" nudge badge
  // in Plan 02 — "Same as last session" instead of "Progressed from last session".
  if (mode === 'last') {
    return { w: last.w, reps: last.reps, rpe: last.rpe ?? null, fromHistory: true, tier: 'last' };
  }

  // 'double' is the only progression mode in v2.3; extensible in future phases.
  if (!mode || mode === 'double') {
    return { ...progressionSuggestion(last, pe), tier: 'cross-session' };
  }

  // Unknown mode → safe fallback to no-history (no inlined rep-advance logic).
  return { ...progressionSuggestion(null, pe), tier: 'no-history' };
}

/**
 * weekOverWeekTrend(sessions, exerciseId) → '▲' | '▼' | null
 *
 * Compares the same exercise's top-set weight across two rolling 7-day windows
 * anchored to Date.now() to produce a directional trend signal for summary cards.
 *
 * sessions    — state.sessions array (completed or active)
 * exerciseId  — the exercise to track (matched against set.exerciseId)
 *
 * Algorithm:
 *   1. Filter to completed sessions (completedAt > 0); active sessions are excluded.
 *   2. Bucket by session.completedAt (Unix ms):
 *      thisWeek = [now-7d, now)   — real-time rolling window
 *      lastWeek = [now-14d, now-7d) — prior rolling window
 *   3. Collect all sets matching exerciseId in each bucket.
 *   4. Metric: top-set weight = Math.max(set.w) per window.
 *      Chosen for simplicity and consistency with progressionSuggestion (also uses last.w).
 *   5. Either window empty → null (graceful absent; caller hides the trend chip).
 *      thisMax > lastMax → '▲'; thisMax < lastMax → '▼'; equal → null.
 */
export function weekOverWeekTrend(sessions, exerciseId) {
  if (!Array.isArray(sessions) || !exerciseId) return null;
  const now = Date.now();
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

  // Exclude active sessions (completedAt null or 0).
  const completed = sessions.filter(s => s && s.completedAt > 0);

  const setsInWindow = (from, to) =>
    completed
      .filter(s => s.completedAt >= from && s.completedAt < to)
      .flatMap(s => (s.sets || []).filter(st => st && st.exerciseId === exerciseId));

  const thisWeek = setsInWindow(now - WEEK_MS, now);
  const lastWeek = setsInWindow(now - 2 * WEEK_MS, now - WEEK_MS);

  // Either window empty → no directional signal.
  if (thisWeek.length === 0 || lastWeek.length === 0) return null;

  const topWeight = sets => {
    const ws = sets.map(s => s.w).filter(w => typeof w === 'number' && !Number.isNaN(w));
    return ws.length ? Math.max(...ws) : null;
  };
  const thisMax = topWeight(thisWeek);
  const lastMax = topWeight(lastWeek);

  // Either window has no valid numeric weights → no directional signal.
  if (thisMax === null || lastMax === null) return null;

  if (thisMax > lastMax) return '▲';
  if (thisMax < lastMax) return '▼';
  return null; // equal performance → no directional signal
}

// ─── Collapse-state + summary helpers (Phase 38, Plan 38-01) ─────────────────
// Pure functions: no React, no DOM, no store references. Safe to import in
// Node smoke harnesses via data: URL (mirrors the pattern used by smoke-36/37).

/**
 * isComplete(peId, sessionSets, nSets) → boolean
 *
 * Returns true when all prescribed sets for a program-exercise have been logged.
 *
 * peId        — program-exercise id to count (string)
 * sessionSets — the session's flat set array ({ peId, ... }[]); null/undefined treated as []
 * nSets       — number of prescribed sets (from resolvedSets / prescriptionFor)
 *
 * Guards:
 *   - nSets <= 0  → false (a zero-set prescription must never report complete, T-38-02)
 *   - empty list  → false (nothing logged)
 *   - Only sets whose peId === peId are counted (cross-exercise sets ignored, T-38-02)
 */
export function isComplete(peId, sessionSets, nSets) {
  if (!nSets || nSets <= 0) return false;
  const sets = Array.isArray(sessionSets) ? sessionSets : [];
  return sets.filter(s => s && s.peId === peId).length >= nSets;
}

/**
 * activeSetIndex(peId, sessionSets, nSets) → number
 *
 * The next set to log for a program-exercise: the lowest set index in [0, nSets)
 * that has no logged entry yet. Returns nSets when every set is already logged
 * (i.e. no active row — the exercise is complete).
 *
 * DERIVED from the session's logged sets, NOT a stored cursor. A stored per-pe
 * cursor (useState) resets to 0 whenever the active-workout component remounts —
 * resuming an in-progress session, or navigating away and back — which strands
 * logging on an already-logged set 0 so no row is loggable. Deriving from
 * session.sets keeps the active row correct across remount/resume, add-set,
 * remove-set, and moving between exercises.
 */
export function activeSetIndex(peId, sessionSets, nSets) {
  const n = typeof nSets === 'number' && nSets > 0 ? nSets : 0;
  const logged = new Set(
    (Array.isArray(sessionSets) ? sessionSets : [])
      .filter(s => s && s.peId === peId)
      .map(s => s.setIdx)
  );
  for (let i = 0; i < n; i++) if (!logged.has(i)) return i;
  return n;
}

/**
 * adaptiveRestSeconds(prescribed, restTotal, restLeft) → seconds
 *
 * Rest duration for the NEXT set. When you log a set while the previous rest
 * timer is still running, the next timer MATCHES the rest you actually took
 * (restTotal − restLeft) instead of restarting at the full prescribed rest — a
 * "dynamic" timer that tracks your real pace. The result is rounded to the
 * nearest 30s and clamped to [30, prescribed] (never longer than the plan, never
 * a 0-second no-rest). When no timer is running (restLeft ≤ 0 — first set of the
 * session, the timer already finished, or auto-start is off) it returns the full
 * prescribed rest.
 */
export function adaptiveRestSeconds(prescribed, restTotal, restLeft) {
  const p = Math.max(0, Number(prescribed) || 0);
  const total = Number(restTotal) || 0;
  const left = Number(restLeft) || 0;
  if (!(left > 0) || !(total > 0)) return p;      // no running rest → full prescribed
  const rested = Math.max(0, total - left);       // seconds actually rested so far
  const rounded = Math.round(rested / 30) * 30;   // nearest 30s
  return Math.min(p, Math.max(30, rounded));      // clamp [30, prescribed]
}

/**
 * isExpanded(peId, expandedByPe, complete) → boolean
 *
 * Derives whether a program-exercise section should be expanded.
 *
 * peId         — program-exercise id (string)
 * expandedByPe — explicit per-pe override map ({ [peId]: boolean }); lookup is per-key only
 * complete     — boolean result of isComplete for this pe (derived collapse signal)
 *
 * Priority formula (WLAY-05):
 *   expandedByPe[peId] !== undefined  → explicit override wins (manual expand OR manual collapse)
 *   otherwise                         → !complete (incomplete = expanded; complete = collapsed)
 *
 * All three priority-inversion cases are covered:
 *   1. No override + incomplete  → true  (default expanded)
 *   2. No override + complete    → false (auto-collapsed)
 *   3. override=true + complete  → true  (manual re-expand wins over auto-collapse)
 *   4. override=false + !complete → false (manual collapse wins over derived expanded)
 *   5. Another pe's override     → unaffected (per-key lookup only)
 */
export function isExpanded(peId, expandedByPe, complete) {
  const overrides = expandedByPe && typeof expandedByPe === 'object' ? expandedByPe : {};
  return overrides[peId] !== undefined ? overrides[peId] : !complete;
}

/**
 * summarizeSets(peSets) → { loggedCount, totalReps, avgW }
 *
 * Aggregates a filtered set of logged sets for one program-exercise into the
 * three statistics displayed on ExerciseSummaryCard (WLAY-04).
 *
 * peSets   — array of logged sets for one pe ({ w, reps, ... }[]); null/undefined treated as []
 *
 * Returns:
 *   loggedCount — number of sets logged
 *   totalReps   — sum of all reps (s.reps || 0 — null/undefined reps treated as 0)
 *   avgW        — volume-weighted average weight: sum(w * reps) / totalReps
 *                 0 when totalReps = 0 (bodyweight / empty; never NaN/Infinity, T-38-01)
 *
 * Bodyweight edge (DEC-38-01): all w=0 → avgW=0. Caller (ExerciseSummaryCard) checks
 *   avgW === 0 && loggedCount > 0 to show "Bodyweight" instead of "0 lb avg".
 */
export function summarizeSets(peSets) {
  const sets = Array.isArray(peSets) ? peSets : [];
  const loggedCount = sets.length;
  let totalReps = 0;
  let volume = 0;
  for (const s of sets) {
    const r = s.reps || 0;
    const w = s.w || 0;
    totalReps += r;
    volume += w * r;
  }
  const avgW = totalReps > 0 ? volume / totalReps : 0;
  return { loggedCount, totalReps, avgW };
}

/**
 * localDateStr(tsMs) → 'YYYY-MM-DD'
 *
 * Converts a Unix millisecond timestamp to a local-timezone YYYY-MM-DD string.
 * Uses local-time getters (getFullYear/getMonth/getDate) so late-evening sessions
 * always bucket to the user's local calendar day rather than the UTC day.
 * Local getters are required — UTC-based date formatting causes off-by-one
 * errors for users in negative UTC offset timezones (SESS-05).
 */
export function localDateStr(tsMs) {
  const d = new Date(tsMs);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Weekday names indexed by Date.getDay() (0=Sunday … 6=Saturday).
// Used by scheduledDayFor to match program.days[].day strings.
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
                       'Thursday', 'Friday', 'Saturday'];

/**
 * scheduledDayFor(dateStr, program) → program.days entry | null
 *
 * Returns the program.days entry whose day.day string matches the local weekday
 * of dateStr ('YYYY-MM-DD'), or null if the date is not a scheduled day.
 *
 * Null-safe: returns null when dateStr is falsy, program is falsy, or
 * program.days is not an array. Empty day.day strings are skipped (day.day &&
 * guard — locked degradation for program days with no weekday set).
 *
 * Uses the 3-argument Date constructor new Date(y, m-1, d) to get local-time
 * day-of-week. Never uses new Date('YYYY-MM-DD') which parses as UTC midnight
 * and may return the wrong local weekday near midnight (Pitfall 4).
 */
export function scheduledDayFor(dateStr, program) {
  if (!dateStr || !program || !Array.isArray(program.days)) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const date = new Date(y, mo - 1, d);          // local time — no UTC shift
  const weekdayName = WEEKDAY_NAMES[date.getDay()];
  return program.days.find(day =>
    day.day && day.day.slice(0, 3).toLowerCase() === weekdayName.slice(0, 3).toLowerCase()
  ) ?? null;
}

/**
 * sessionLifecycleFor({ date, dayId, programId, sessions, activeSessionId, _today })
 *   → 'completed' | 'in-progress' | 'not-started' | 'missed' | 'future' | 'rest'
 *
 * Pure derived-state function — never stored. Returns the single canonical
 * lifecycle state for a specific (program, day, date) triple.
 *
 * Decision tree (order is locked — T-12 enforces that completedAt fires before
 * in-progress):
 *   1. !dayId          → 'rest'      (not a scheduled day)
 *   2. any onDay completedAt         → 'completed'
 *   3. date > today                  → 'future'
 *   4. date === today:
 *        activeSessionId match       → 'in-progress'
 *        else                        → 'not-started'
 *   5. date < today:
 *        no program sessions         → 'not-started'  (program not started)
 *        date < earliest session     → 'not-started'  (before program started)
 *        else                        → 'missed'
 *
 * _today: test-injection seam (defaults to localDateStr(Date.now())).
 *         Never passed by production callers — Claude's discretion per CONTEXT.md.
 */
export function sessionLifecycleFor({
  date, dayId, programId, sessions, activeSessionId, _today,
}) {
  // 1. No scheduled day for this date
  if (!dayId) return 'rest';

  const today = _today ?? localDateStr(Date.now());
  const sess = Array.isArray(sessions) ? sessions : [];

  // Sessions for this specific (programId, dayId, date) triple
  const onDay = sess.filter(ss =>
    ss.programId === programId &&
    ss.dayId === dayId &&
    localDateStr(ss.startedAt) === date
  );

  // 2. Completed session exists for this date (MUST precede in-progress check — T-12)
  if (onDay.some(ss => ss.completedAt)) return 'completed';

  // 3. Future date
  if (date > today) return 'future';

  // 4. Today
  if (date === today) {
    const inProgress = onDay.find(ss => ss.id === activeSessionId && !ss.completedAt);
    return inProgress ? 'in-progress' : 'not-started';
  }

  // 5. Past date — 4-condition missed predicate
  // Condition 4: date >= earliest session for this program (any day)
  const programSessions = sess.filter(ss => ss.programId === programId && ss.startedAt);
  if (programSessions.length === 0) return 'not-started'; // program not yet started
  const earliestDate = programSessions
    .map(ss => localDateStr(ss.startedAt))
    .sort()[0];
  if (date < earliestDate) return 'not-started'; // before program started
  // All 4 conditions met: date < today, no completedAt, dayId not null, date >= earliest
  return 'missed';
}

/**
 * calendarCellState(dateStr, program, sessions, activeSessionId) → state string
 *
 * Thin wrapper over sessionLifecycleFor that resolves dayId from the program
 * via scheduledDayFor, then delegates to sessionLifecycleFor.
 * Use when you have the program object; use sessionLifecycleFor directly when
 * you already have dayId pre-resolved (e.g., Phase 42 home box).
 */
export function calendarCellState(dateStr, program, sessions, activeSessionId) {
  // Any workout completed that day counts — including freestyle sessions and
  // sessions from other programs.
  if (Array.isArray(sessions) && sessions.some(ss => ss.completedAt && localDateStr(ss.startedAt) === dateStr)) return 'completed';
  if (!program) return 'rest';
  const day = scheduledDayFor(dateStr, program);
  return sessionLifecycleFor({
    date: dateStr,
    dayId: day?.id ?? null,
    programId: program.id,
    sessions,
    activeSessionId,
  });
}

/**
 * monthGridCells(year, month, program, sessions, activeSessionId) → cell[]
 *
 * Returns a 42-element array (6×7 grid) of calendar cells for the given month.
 * month is 1-indexed (1=January, 12=December).
 *
 * Each cell: { date: 'YYYY-MM-DD', inMonth: boolean, state: string }
 *
 * Out-of-month cells (padding before the 1st and after the last) get
 * state: 'out-of-range'. In-month cells get state from calendarCellState.
 */
export function monthGridCells(year, month, program, sessions, activeSessionId) {
  const firstOfMonth = new Date(year, month - 1, 1);
  // Monday-first grid: shift getDay() (0=Sunday) so Monday is column 0 and
  // Sunday is column 6. Sun→6, Mon→0, … Sat→5.
  const startPad = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = 0; i < 42; i++) {
    const dayOffset = i - startPad + 1;
    const d = new Date(year, month - 1, dayOffset);
    const y2 = d.getFullYear();
    const m2 = String(d.getMonth() + 1).padStart(2, '0');
    const d2 = String(d.getDate()).padStart(2, '0');
    const dateStr = `${y2}-${m2}-${d2}`;
    const inMonth = dayOffset >= 1 && dayOffset <= daysInMonth;
    if (!inMonth) {
      cells.push({ date: dateStr, inMonth: false, state: 'out-of-range' });
    } else {
      const state = calendarCellState(dateStr, program, sessions, activeSessionId);
      cells.push({ date: dateStr, inMonth: true, state });
    }
  }
  return cells;
}

/**
 * exerciseNameFor(state, exId) → string
 *
 * Resolves a logged exercise id (as stored on sets/PRs) to a display name.
 * Imported programs (e.g. Nippard `nip_*` ids) add their exercises to
 * state.programs but NOT to the state.exercises library, so a library-only
 * lookup falls back to the generic 'Exercise' for every imported PR. Resolve
 * via the library first, then any program-day exercise carrying the same
 * exerciseId (those rows hold both exerciseId and a human name), then 'Exercise'.
 */
export function exerciseNameFor(state, exId) {
  if (!exId) return 'Exercise';
  const lib = state?.exercises?.find(e => e.id === exId);
  if (lib?.name) return lib.name;
  for (const p of state?.programs || []) {
    for (const d of p.days || []) {
      const pe = (d.exercises || []).find(e => e.exerciseId === exId);
      if (pe?.name) return pe.name;
    }
  }
  return 'Exercise';
}

/**
 * activeSessionOf({ sessions, activeSessionId })
 *   → session object | null
 *
 * NAV-02 resume selector. Returns the session whose id === activeSessionId,
 * with its sets[] array intact (no clone, no strip — resume fidelity requires
 * the original reference so logged sets are always accessible).
 *
 * Returns null when:
 *   - sessions is not an array
 *   - activeSessionId is null/undefined/falsy
 *   - no session matches the id
 *
 * Keep free of React/store imports so it remains data:-importable.
 */
export function activeSessionOf({ sessions, activeSessionId }) {
  if (!Array.isArray(sessions) || !activeSessionId) return null;
  return sessions.find(s => s.id === activeSessionId) || null;
}

/**
 * wouldOrphanActiveSession({ sessions, activeSessionId, dayId })
 *   → boolean
 *
 * NAV-03 concurrent-session guard predicate. Returns true only when starting
 * a session for `dayId` would orphan an existing live (not-completed) session
 * that belongs to a DIFFERENT day.
 *
 * Decision tree:
 *   1. activeSessionId falsy                       → false (nothing active)
 *   2. sessions not an array                       → false (unusable args)
 *   3. no session matches activeSessionId          → false (stale pointer — nothing live to orphan)
 *   4. active session has completedAt set          → false (already done)
 *   5. active session dayId === requested dayId   → false (same-day resume — never blocked)
 *   6. otherwise (live session on different day)  → true
 *
 * Single source of truth consumed by store.jsx (plan 45-02) and
 * screens_wired.jsx (plan 45-03). Keep free of React/store imports.
 */
export function wouldOrphanActiveSession({ sessions, activeSessionId, dayId }) {
  if (!activeSessionId) return false;
  if (!Array.isArray(sessions)) return false;
  const active = sessions.find(s => s.id === activeSessionId);
  if (!active) return false;             // stale pointer
  if (active.completedAt) return false;  // already completed
  if (active.dayId === dayId) return false; // same-day resume
  return true;
}
